import {useCallback, useEffect, useRef} from 'react';
import {serverTimestamp} from 'firebase/database';
import {useDocumentData} from 'react-firebase-hooks/firestore';
import {useIdleTimer} from 'react-idle-timer';

import {
  COURSE_PRESENCE_LAST_ACTIVE_FIELD,
  getCoursePresenceCheckInIntervalMs,
  getCoursePresenceUserPath,
  isCoursePresenceCheckInDue,
} from '@cip/core/coursePresence';
import {getCourseDocumentRef} from '@cip/core/firebase/client';
import {database} from 'firebaseApp';

/** Minimum gap between activity events the idle timer reports. */
const ACTIVITY_THROTTLE_MS = 5 * 1000;

/**
 * Records the signed-in user's activity in the course presence system while
 * the course's settings call for it. The user checks in on arrival and then on
 * activity, at most once per interval; an idle user writes nothing.
 *
 * Writes nothing for courses that have not opted in, so the legacy heartbeat in
 * `components/auth/Authenticated.tsx` stays their only tracking.
 *
 * @param args.uid - The signed-in user, or undefined when signed out, which
 *   disables check-ins.
 * @param args.courseId - The course in view, or an empty string outside a
 *   course, which disables check-ins.
 * @return Nothing.
 * @throws Never. Failed writes are logged at debug level.
 */
export function useCoursePresenceCheckIn({
  uid,
  courseId,
}: {
  uid: string | undefined;
  courseId: string;
}): void {
  const [course] = useDocumentData(
    courseId ? getCourseDocumentRef({courseId}) : undefined,
  );
  const intervalMs = getCoursePresenceCheckInIntervalMs({course});
  const lastCheckInMsRef = useRef<number | null>(null);

  const checkIn = useCallback(() => {
    if (!uid || !courseId || intervalMs === null) {
      return;
    }
    const nowMs = Date.now();
    if (
      !isCoursePresenceCheckInDue({
        lastCheckInMs: lastCheckInMsRef.current,
        nowMs,
        intervalMs,
      })
    ) {
      return;
    }
    lastCheckInMsRef.current = nowMs;
    database
      .ref(getCoursePresenceUserPath({courseId, uid}))
      .update({[COURSE_PRESENCE_LAST_ACTIVE_FIELD]: serverTimestamp()})
      .catch(error => {
        console.debug('[coursePresence] check-in failed', error);
      });
  }, [uid, courseId, intervalMs]);

  useEffect(() => {
    lastCheckInMsRef.current = null;
    checkIn();
  }, [checkIn]);

  useIdleTimer({onAction: checkIn, throttle: ACTIVITY_THROTTLE_MS});
}
